import { createApi } from '@reduxjs/toolkit/query/react';

import { WORKLIST_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export const worklistApi = createApi({
    baseQuery: getBaseQuery(WORKLIST_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'worklistApi',
    endpoints: (build) => ({
        postClearWorklistTasks: build.mutation<void, { taskIds: string[] }>({
            query: ({ taskIds }) => ({
                url: 'tasks/clear',
                method: 'POST',
                body: taskIds,
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred clearing tasks.'),
        }),
        postRebillAttestation: build.mutation<void, { taskId: string }>({
            query: ({ taskId }) => ({
                url: `tasks/attest/${taskId}`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred submitting attestation.',
            ),
        }),
        postUnableToCompleteTask: build.mutation<void, { taskId: string; reason: string }>({
            query: ({ taskId, reason }) => ({
                url: `tasks/move/${taskId}`,
                method: 'POST',
                body: { reason },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred confirming task as unable to complete.',
            ),
        }),
        postConfirmNeedsByDateTasks: build.mutation<void, { referralId: string }>({
            query: ({ referralId }) => ({
                url: `tasks/confirm_needs_by_date/${referralId}`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError(
                'Unable to update needs by date. Please try again.',
            ),
        }),
        postConfirmLddAccess: build.mutation<void, { taskId: string }>({
            query: ({ taskId }) => ({
                url: `tasks/confirm_ldd_capability/${taskId}`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred confirming LDD access.'),
        }),
        postDenyLddAccess: build.mutation<void, { taskId: string }>({
            query: ({ taskId }) => ({
                url: `tasks/deny_ldd_capability/${taskId}`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred denying LDD access.'),
        }),
        postConvert: build.mutation<void, { taskId: string }>({
            query: ({ taskId }) => ({
                url: `tasks/convert/${taskId}`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred marking the task as converted.',
            ),
        }),
        postDoNotConvert: build.mutation<void, { taskId: string }>({
            query: ({ taskId }) => ({
                url: `tasks/do_not_convert/${taskId}`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred marking the task as did not convert.',
            ),
        }),
        putTaskTypeOptOut: build.mutation<
            void,
            { taskType: string; taskTypeDisplayName: string; enabled: boolean }
        >({
            query: ({ taskType, enabled }) => ({
                url: `tasks/task_type_opt_out/${taskType}`,
                method: 'PUT',
                body: { enabled },
            }),
            transformErrorResponse: (error, _meta, arg) =>
                handleRTKQueryError(`An error occurred blocking ${arg.taskTypeDisplayName} tasks.`)(
                    error,
                ),
        }),
    }),
});

export const {
    usePostClearWorklistTasksMutation,
    usePostRebillAttestationMutation,
    usePostUnableToCompleteTaskMutation,
    usePostConfirmNeedsByDateTasksMutation,
    usePostConfirmLddAccessMutation,
    usePostDenyLddAccessMutation,
    usePostConvertMutation,
    usePostDoNotConvertMutation,
    usePutTaskTypeOptOutMutation,
} = worklistApi;
